import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  supportedLocales,
  translations,
  type SupportedLocale,
  type TranslationKey,
} from './translations'

type LocaleContextValue = {
  locale: SupportedLocale
  setLocale: (locale: SupportedLocale) => void
  resetLocaleToBrowser: () => void
  t: (key: TranslationKey | string) => string
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
  formatDate: (value: string | Date) => string
  formatDateTime: (value: string | Date) => string
  formatTime: (value: string | Date) => string
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function detectBrowserLocale(): SupportedLocale {
  const candidates = navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language]
  const language = candidates.find((candidate) =>
    supportedLocales.some((locale) =>
      locale.toLowerCase().startsWith(candidate.slice(0, 2).toLowerCase()),
    ),
  )

  return supportedLocales.find((locale) =>
    locale.toLowerCase().startsWith(language?.slice(0, 2).toLowerCase() ?? ''),
  ) ?? 'es-ES'
}

function detectLocale(): SupportedLocale {
  const saved = localStorage.getItem('apl-enterprise.locale')
  if (saved && supportedLocales.includes(saved as SupportedLocale)) {
    return saved as SupportedLocale
  }
  return detectBrowserLocale()
}

export function LocaleProvider({ children }: PropsWithChildren) {
  const [locale, updateLocale] = useState<SupportedLocale>(detectLocale)
  const setLocale = useCallback((nextLocale: SupportedLocale) => {
    localStorage.setItem('apl-enterprise.locale', nextLocale)
    document.documentElement.lang = nextLocale
    updateLocale(nextLocale)
  }, [])
  const resetLocaleToBrowser = useCallback(() => {
    const browserLocale = detectBrowserLocale()
    localStorage.removeItem('apl-enterprise.locale')
    document.documentElement.lang = browserLocale
    updateLocale(browserLocale)
  }, [])

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      setLocale,
      resetLocaleToBrowser,
      t: (key) => translations[locale][key as TranslationKey] ||
        translations['es-ES'][key as TranslationKey] || String(key),
      formatNumber: (number, options) =>
        new Intl.NumberFormat(locale, options).format(number),
      formatDate: (date) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          ...(typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
            ? { timeZone: 'UTC' }
            : {}),
        }).format(typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
          ? new Date(`${date}T00:00:00Z`)
          : typeof date === 'string' ? new Date(date) : date),
      formatDateTime: (date) =>
        new Intl.DateTimeFormat(locale, {
          dateStyle: 'medium',
          timeStyle: 'short',
        }).format(typeof date === 'string' ? new Date(date) : date),
      formatTime: (date) =>
        new Intl.DateTimeFormat(locale, { timeStyle: 'short' })
          .format(typeof date === 'string' ? new Date(date) : date),
    }),
    [locale, resetLocaleToBrowser, setLocale],
  )

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
}

export function useLocale() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocale must be used inside LocaleProvider')
  }
  return context
}
